import { useMutation, useQueryClient } from "@tanstack/react-query";
import WorkspacesService from "../services/WorkspacesService";
import { useToastAlert } from "../components/app/ToastAlert/ToastAlertProvider";
import { useCurrentWorkspace } from "./useCurrentWorkspace";
import {
  BrandedAppStatus,
  UserInfoDto,
} from "@growth-machine-llc/stridist-api-client";
import { CURRENT_USER_QUERY_KEY } from "../wrappers/current-user/CurrentUserWrapper";

interface UseRequestBrandedAppOptions {
  onSuccess?: () => void;
}

export function useRequestBrandedApp(options?: UseRequestBrandedAppOptions) {
  const { showToastAlert } = useToastAlert();
  const queryClient = useQueryClient();
  const { id: workspaceId } = useCurrentWorkspace();

  const { mutate: requestBrandedApp, isPending } = useMutation({
    mutationFn: WorkspacesService.requestBrandedApp,
    onSuccess: () => {
      showToastAlert("success", {
        message:
          "Branded app requested successfully! Our team will contact you shortly.",
      });
      queryClient.setQueriesData(
        {
          queryKey: [CURRENT_USER_QUERY_KEY],
        },
        (oldData: UserInfoDto) => {
          if (!oldData?.workspace) {
            return oldData;
          }

          return UserInfoDto.fromJS({
            ...oldData,
            workspace: {
              ...oldData.workspace,
              brandedApp: {
                status: BrandedAppStatus.Requested,
              },
            },
          });
        },
      );
      options?.onSuccess?.();
    },
    onError: () => {
      showToastAlert("error", {
        message: "Failed to request branded app. Please try again later.",
      });
    },
  });

  const handleRequestBrandedApp = () => {
    requestBrandedApp({ workspaceId });
  };

  return {
    requestBrandedApp: handleRequestBrandedApp,
    isPending,
  };
}
